/**
 * 基本設計の画面の「自分だけの見え方」（節の並び・畳んだまとまり・表かカードか・隠した列・
 * 図の列の表示と向き）。ブラウザの localStorage に、見ている人のメールごとに置く。
 *
 * 見え方は人ごとに違ってよく、基本設計の中身でもレビューの対象でもないので API には送らない。
 * 同じブラウザを別の人が使っても混ざらないよう、キーにメールを入れる。
 *
 * localStorage は private モードや容量超過で読み書きが例外になることがある。見え方が残らない
 * だけで画面は動くので、例外は記録して既定の見え方に落とす。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計の画面の個人の見え方（節の並び・畳み・表/カード・隠す列・図の列と向き）を、見ている人ごとにブラウザへ保存して読む
 * @graph-connects none
 */

import { logEvent } from '@cortex/faro';

/** 図の向き。LR は左から右、TB は上から下。 @graph-connects none */
export type BdGraphDirection = 'LR' | 'TB';

/** 節の行の見せ方。 @graph-connects none */
export type BdRowsLayout = 'cards' | 'table';

/** 個人の見え方。 @graph-connects none */
export interface BdViewSettings {
  /** ナビでの節の並び（節の名前）。ここに無い節は定義の順で後ろに付く */
  order: string[];
  /** 畳んだナビのまとまり */
  collapsed: string[];
  /** 節ごとの見せ方。無い節はカード */
  layouts: Record<string, BdRowsLayout>;
  /** 節ごとの隠した列 */
  hiddenColumns: Record<string, string[]>;
  /** 図で隠した列（0 始まりの列の番号） */
  hiddenLayers: number[];
  direction: BdGraphDirection;
}

/** 既定の見え方。 @graph-connects none */
export const DEFAULT_BD_VIEW_SETTINGS: BdViewSettings = {
  order: [],
  collapsed: [],
  layouts: {},
  hiddenColumns: {},
  hiddenLayers: [],
  direction: 'LR',
};

/** 保存のキーの頭。形を変えるときは版を上げ、古い形を読まないようにする。 @graph-connects none */
const KEY_PREFIX = 'cortex.bd.view.v1:';

/** 見ている人ごとのキー。メールが無い（未ログイン）ときは共通のキー。 @graph-connects none */
export function bdViewSettingsKey(email: string | null | undefined): string {
  const who = email?.trim().toLowerCase() ?? '';
  return `${KEY_PREFIX}${who === '' ? 'anonymous' : who}`;
}

/** 文字列の配列だけを残す。 @graph-connects none */
function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

/** 値が普通のオブジェクトか。 @graph-connects none */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * 保存された値を見え方に戻す。手で書き換えられた値や古い版の値でも画面が壊れないよう、
 * 形の合わないところは既定に落とす。
 * @graph-connects none
 */
export function parseBdViewSettings(raw: string | null): BdViewSettings {
  if (raw === null) return DEFAULT_BD_VIEW_SETTINGS;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch (error) {
    logEvent('bd_view_settings_parse_failed', {
      error_message: error instanceof Error ? error.message : String(error),
    });
    return DEFAULT_BD_VIEW_SETTINGS;
  }
  if (!isRecord(value)) return DEFAULT_BD_VIEW_SETTINGS;
  const layouts = isRecord(value.layouts)
    ? Object.fromEntries(
        Object.entries(value.layouts).filter(
          (entry): entry is [string, BdRowsLayout] => entry[1] === 'cards' || entry[1] === 'table',
        ),
      )
    : {};
  const hiddenColumns = isRecord(value.hiddenColumns)
    ? Object.fromEntries(
        Object.entries(value.hiddenColumns).map(([key, columns]) => [key, stringList(columns)]),
      )
    : {};
  const hiddenLayers = Array.isArray(value.hiddenLayers)
    ? value.hiddenLayers.filter((v): v is number => typeof v === 'number' && Number.isInteger(v))
    : [];
  return {
    order: stringList(value.order),
    collapsed: stringList(value.collapsed),
    layouts,
    hiddenColumns,
    hiddenLayers,
    direction: value.direction === 'TB' ? 'TB' : 'LR',
  };
}

/**
 * 見ている人の見え方を読む。読めなければ既定。
 * @graph-connects parseBdViewSettings [calls] 保存された値の読み戻し
 */
export function loadBdViewSettings(email: string | null | undefined): BdViewSettings {
  try {
    return parseBdViewSettings(window.localStorage.getItem(bdViewSettingsKey(email)));
  } catch (error) {
    logEvent('bd_view_settings_load_failed', {
      error_message: error instanceof Error ? error.message : String(error),
    });
    return DEFAULT_BD_VIEW_SETTINGS;
  }
}

/**
 * 見ている人の見え方を書く。書けなければ記録だけ残す（画面の見え方はそのまま使える）。
 * @graph-connects none
 */
export function saveBdViewSettings(
  email: string | null | undefined,
  settings: BdViewSettings,
): void {
  try {
    window.localStorage.setItem(bdViewSettingsKey(email), JSON.stringify(settings));
  } catch (error) {
    logEvent('bd_view_settings_save_failed', {
      error_message: error instanceof Error ? error.message : String(error),
    });
  }
}

/** 並びの中の値を入れ替えたり外したりする。 @graph-connects none */
export function toggleIn<T>(list: readonly T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

/**
 * 節を今の並びの中で 1 つ前（-1）か後ろ（+1）へ動かす。`keys` は画面に今出ている並び。
 * 端を越える動きは何もしない。
 * @graph-connects none
 */
export function moveInOrder(keys: readonly string[], key: string, delta: -1 | 1): string[] {
  const index = keys.indexOf(key);
  const target = index + delta;
  if (index < 0 || target < 0 || target >= keys.length) return [...keys];
  const next = keys.filter((k) => k !== key);
  next.splice(target, 0, key);
  return next;
}

/**
 * 並びを当てる。`order` にある項目を先にその順で、無い項目を元の順で後ろに並べる。
 * @graph-connects none
 */
export function applyOrder<T>(
  items: readonly T[],
  order: readonly string[],
  keyOf: (item: T) => string,
): T[] {
  const rank = new Map(order.map((key, i) => [key, i]));
  return items
    .map((item, i) => ({ item, i, r: rank.get(keyOf(item)) }))
    .sort((a, b) => {
      if (a.r !== undefined && b.r !== undefined) return a.r - b.r;
      if (a.r !== undefined) return -1;
      if (b.r !== undefined) return 1;
      return a.i - b.i;
    })
    .map((x) => x.item);
}
